import { useCallback, useEffect, useRef, useState } from "react";

// EK-442 — Text-to-Speech via Web Speech API (SpeechSynthesis).
// Seleciona voz pt-BR, aplica rate/pitch/volume das preferências e permite
// interromper a leitura a qualquer momento.

export const isTTSSupported = (): boolean => typeof window !== "undefined" && "speechSynthesis" in window;

export interface UseTTSOptions {
	lang?: string;
	rate?: number;
	pitch?: number;
	volume?: number;
	/** voiceURI preferido; se ausente, escolhe a primeira voz pt-BR disponível. */
	voiceURI?: string | null;
	onStart?: () => void;
	onEnd?: () => void;
}

export interface UseTTSReturn {
	supported: boolean;
	speaking: boolean;
	/** Vozes pt-BR disponíveis no sistema (para o seletor de preferências). */
	voices: SpeechSynthesisVoice[];
	speak: (text: string) => void;
	cancel: () => void;
}

export function useTTS(options: UseTTSOptions = {}): UseTTSReturn {
	const { lang = "pt-BR", rate = 1, pitch = 1, volume = 1, voiceURI = null, onStart, onEnd } = options;

	const supported = isTTSSupported();
	const [speaking, setSpeaking] = useState(false);
	const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

	const optsRef = useRef({ lang, rate, pitch, volume, voiceURI, onStart, onEnd });
	useEffect(() => {
		optsRef.current = { lang, rate, pitch, volume, voiceURI, onStart, onEnd };
	}, [lang, rate, pitch, volume, voiceURI, onStart, onEnd]);

	// Vozes carregam de forma assíncrona; ouvimos `voiceschanged`.
	useEffect(() => {
		if (!supported) return;
		const synth = window.speechSynthesis;
		const loadVoices = () => {
			const all = synth.getVoices();
			const ptVoices = all.filter((v) => v.lang?.toLowerCase().startsWith("pt"));
			setVoices(ptVoices.length > 0 ? ptVoices : all);
		};
		loadVoices();
		synth.addEventListener?.("voiceschanged", loadVoices);
		return () => synth.removeEventListener?.("voiceschanged", loadVoices);
	}, [supported]);

	const pickVoice = useCallback((): SpeechSynthesisVoice | null => {
		if (!supported) return null;
		const all = window.speechSynthesis.getVoices();
		const { voiceURI: preferred, lang: targetLang } = optsRef.current;
		if (preferred) {
			const match = all.find((v) => v.voiceURI === preferred);
			if (match) return match;
		}
		const ptMatch = all.find((v) => v.lang?.toLowerCase().startsWith(targetLang.slice(0, 2).toLowerCase()));
		return ptMatch ?? all[0] ?? null;
	}, [supported]);

	// Guarda o utterance "vigente" — usado para descartar callbacks tardios de
	// utterances cancelados (que disparariam `onEnd` no consumidor erroneamente).
	const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
	const pendingSpeakTimerRef = useRef<number | null>(null);
	const startWatchdogRef = useRef<number | null>(null);

	const clearPendingSpeak = useCallback(() => {
		if (pendingSpeakTimerRef.current !== null) {
			window.clearTimeout(pendingSpeakTimerRef.current);
			pendingSpeakTimerRef.current = null;
		}
	}, []);

	const clearStartWatchdog = useCallback(() => {
		if (startWatchdogRef.current !== null) {
			window.clearTimeout(startWatchdogRef.current);
			startWatchdogRef.current = null;
		}
	}, []);

	const cancel = useCallback(() => {
		if (!supported) return;
		clearPendingSpeak();
		clearStartWatchdog();
		currentUtteranceRef.current = null;
		window.speechSynthesis.cancel();
		setSpeaking(false);
	}, [supported, clearPendingSpeak, clearStartWatchdog]);

	const speak = useCallback(
		(text: string) => {
			if (!supported || !text.trim()) return;
			const synth = window.speechSynthesis;

			const enqueue = (attempt: number) => {
				const utterance = new SpeechSynthesisUtterance(text);
				const { lang: uLang, rate: uRate, pitch: uPitch, volume: uVolume } = optsRef.current;
				utterance.lang = uLang;
				utterance.rate = uRate;
				utterance.pitch = uPitch;
				utterance.volume = uVolume;
				const voice = pickVoice();
				if (voice) utterance.voice = voice;

				currentUtteranceRef.current = utterance;

				utterance.onstart = () => {
					if (currentUtteranceRef.current !== utterance) return;
					clearStartWatchdog();
					setSpeaking(true);
					optsRef.current.onStart?.();
				};
				utterance.onend = () => {
					if (currentUtteranceRef.current !== utterance) return;
					clearStartWatchdog();
					currentUtteranceRef.current = null;
					setSpeaking(false);
					optsRef.current.onEnd?.();
				};
				utterance.onerror = (ev) => {
					if (currentUtteranceRef.current !== utterance) return;
					clearStartWatchdog();
					currentUtteranceRef.current = null;
					setSpeaking(false);
					const err = (ev as SpeechSynthesisErrorEvent)?.error;
					if (err !== "canceled" && err !== "interrupted") {
						// eslint-disable-next-line no-console
						console.warn("[useTTS] speechSynthesis error:", err);
					}
					optsRef.current.onEnd?.();
				};

				// Garante que o engine não está paused (pause silencioso pode persistir
				// entre interações no Chrome) — sem isso, speak() entra na fila mas
				// onstart nunca dispara e a fala fica em silêncio indefinido.
				try {
					synth.resume();
				} catch {
					/* noop */
				}

				synth.speak(utterance);

				// Watchdog: se em 1.5s o onstart não disparou e nada está soando,
				// reciclamos o engine (cancel + nova tentativa) — até 2 tentativas.
				clearStartWatchdog();
				startWatchdogRef.current = window.setTimeout(() => {
					startWatchdogRef.current = null;
					if (currentUtteranceRef.current !== utterance) return;
					if (synth.speaking) return; // já começou; tudo certo
					if (attempt < 2) {
						// eslint-disable-next-line no-console
						console.warn("[useTTS] onstart não disparou em 1.5s — retry", attempt + 1);
						try {
							synth.cancel();
						} catch {
							/* noop */
						}
						pendingSpeakTimerRef.current = window.setTimeout(() => {
							pendingSpeakTimerRef.current = null;
							enqueue(attempt + 1);
						}, 200);
					} else {
						// Desiste e libera o consumidor para seguir o fluxo.
						// eslint-disable-next-line no-console
						console.warn("[useTTS] falha persistente ao iniciar fala — abortando");
						currentUtteranceRef.current = null;
						setSpeaking(false);
						optsRef.current.onEnd?.();
					}
				}, 1500);
			};

			// Bug Chrome: synth.cancel() seguido de synth.speak() na mesma stack frame
			// engole o speak. Sempre que há fala em andamento, separamos os dois com
			// um pequeno delay assíncrono. Sem nada para cancelar, dispara direto.
			clearPendingSpeak();
			clearStartWatchdog();
			if (synth.speaking || synth.pending) {
				currentUtteranceRef.current = null;
				try {
					synth.cancel();
				} catch {
					/* noop */
				}
				pendingSpeakTimerRef.current = window.setTimeout(() => {
					pendingSpeakTimerRef.current = null;
					enqueue(0);
				}, 120);
			} else {
				enqueue(0);
			}
		},
		[supported, pickVoice, clearPendingSpeak, clearStartWatchdog],
	);

	// Cancela a fala ao desmontar para não vazar áudio entre telas.
	useEffect(() => {
		return () => {
			clearPendingSpeak();
			clearStartWatchdog();
			currentUtteranceRef.current = null;
			if (supported) window.speechSynthesis.cancel();
		};
	}, [supported, clearPendingSpeak, clearStartWatchdog]);

	return { supported, speaking, voices, speak, cancel };
}
